import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ShoppingBag } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useSettings } from "../context/SettingsContext";

const LOGO_URL = "/slsa_logo_transparent.png";

const navLinks = [
    { href: "/", label: "Home" },
    { href: "/about", label: "About" },
    { href: "/events", label: "Events" },
    { href: "/whats-happening", label: "What's Happening" },
    { href: "/merch", label: "Shop" },
    { href: "/gallery", label: "Gallery" },
    { href: "/sponsors", label: "Sponsors" },
    { href: "/get-involved", label: "Get Involved" },
    { href: "/contact", label: "Contact" },
];

export const Navbar = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();
    const { count, setCartOpen } = useCart();
    const { settings } = useSettings();
    const storeOn = !!settings?.store_enabled;
    const links = storeOn ? navLinks : navLinks.filter(l => l.href !== "/merch");

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 30);
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    useEffect(() => { setIsOpen(false); }, [location]);

    return (
        <nav
            data-testid="navbar"
            className={`fixed top-0 left-0 right-0 z-50 transition-shadow duration-300 bg-[#3D2008] ${scrolled ? "shadow-[0_4px_20px_rgba(0,0,0,0.4)]" : ""}`}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16">
                    {/* Logo */}
                    <Link to="/" className="flex items-center gap-3" data-testid="navbar-logo">
                        <img
                            src={LOGO_URL}
                            alt="SLSA Logo"
                            className="h-10 w-auto max-w-[120px] object-contain"
                        />
                        <div className="hidden sm:block leading-tight">
                            <div className="font-anton text-[#F5EDD6] text-sm uppercase tracking-widest">Seeley Lake</div>
                            <div className="font-anton text-[#C4522A] text-xs uppercase tracking-widest">Skatepark Assoc.</div>
                        </div>
                    </Link>

                    {/* Desktop nav */}
                    <div className="hidden lg:flex items-center gap-1">
                        {links.map((link) => (
                            <Link
                                key={link.href}
                                to={link.href}
                                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                                className={`px-3 py-2 font-manrope text-sm font-medium uppercase tracking-wide transition-colors duration-200 ${
                                    location.pathname === link.href
                                        ? "text-[#C4522A]"
                                        : "text-[#F5EDD6] hover:text-[#C9A84C]"
                                }`}
                            >
                                {link.label}
                            </Link>
                        ))}
                        {/* Cart icon */}
                        {storeOn && (
                        <button
                            onClick={() => setCartOpen(true)}
                            data-testid="navbar-cart-btn"
                            className="relative ml-2 p-2 text-[#F5EDD6] hover:text-[#C9A84C] transition-colors"
                            aria-label="Open cart"
                        >
                            <ShoppingBag size={20} />
                            {count > 0 && (
                                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-[#C4522A] text-white text-xs font-bold rounded-full flex items-center justify-center border border-[#C9A84C]">
                                    {count}
                                </span>
                            )}
                        </button>
                        )}
                        <Link
                            to="/donate"
                            data-testid="nav-donate-btn"
                            className="ml-2 px-5 py-2 bg-[#C4522A] text-white font-anton uppercase tracking-wider text-sm border-2 border-[#C9A84C] shadow-[3px_3px_0px_0px_rgba(201,168,76,0.7)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-transform duration-150"
                        >
                            Donate
                        </Link>
                    </div>

                    {/* Mobile hamburger */}
                    <button
                        onClick={() => setIsOpen(!isOpen)}
                        data-testid="mobile-menu-btn"
                        className="lg:hidden p-2 text-[#F5EDD6] hover:text-[#C4522A] transition-colors"
                        aria-label="Toggle menu"
                    >
                        {isOpen ? <X size={24} /> : <Menu size={24} />}
                    </button>
                </div>
            </div>

            {/* Mobile menu */}
            {isOpen && (
                <div className="lg:hidden bg-[#3D2008] border-t-2 border-[#C9A84C]/40 px-4 pb-4" data-testid="mobile-menu">
                    {links.map((link) => (
                        <Link
                            key={link.href}
                            to={link.href}
                            className={`block py-3 font-manrope text-sm uppercase tracking-wide border-b border-[#C9A84C]/20 transition-colors duration-200 ${
                                location.pathname === link.href ? "text-[#C4522A]" : "text-[#F5EDD6] hover:text-[#C4522A]"
                            }`}
                        >
                            {link.label}
                        </Link>
                    ))}
                    <Link
                        to="/donate"
                        data-testid="mobile-donate-btn"
                        className="block mt-4 text-center py-3 bg-[#C4522A] text-white font-anton uppercase tracking-wider text-sm border-2 border-[#C9A84C]"
                    >
                        Donate Now
                    </Link>
                </div>
            )}
        </nav>
    );
};

export default Navbar;
